import axios, { type AxiosError, type AxiosResponse } from "axios";
import { env } from "@/env";
import { Sentry } from "@/sentry";

// Extend the AxiosRequestConfig interface to include our retry flag
declare module "axios" {
	interface InternalAxiosRequestConfig {
		_retry?: boolean;
		_requestStartTime?: number;
	}
}

/**
 * Classifies the type of network/API error for better Sentry categorization
 */
type ApiErrorType =
	| "cors_error"
	| "network_error"
	| "timeout_error"
	| "dns_error"
	| "ssl_error"
	| "connection_refused"
	| "http_error"
	| "request_aborted"
	| "unknown_error";

function classifyError(error: AxiosError): ApiErrorType {
	// No response means the request never completed
	if (!error.response) {
		const message = error.message?.toLowerCase() || "";

		// CORS errors typically show as "Network Error" with no response
		// and the request was made but blocked by browser
		if (error.request && message.includes("network error")) {
			// Check if it's likely CORS by examining the request
			const requestURL = error.config?.url || "";
			const baseURL = error.config?.baseURL || "";
			const fullURL = requestURL.startsWith("http")
				? requestURL
				: `${baseURL}${requestURL}`;

			// If requesting a different origin, it's likely CORS
			if (fullURL && !fullURL.startsWith(window.location.origin)) {
				return "cors_error";
			}
			return "network_error";
		}

		if (message.includes("timeout") || error.code === "ECONNABORTED") {
			return "timeout_error";
		}

		if (message.includes("dns") || error.code === "ENOTFOUND") {
			return "dns_error";
		}

		if (
			message.includes("ssl") ||
			message.includes("certificate") ||
			error.code === "CERT_HAS_EXPIRED"
		) {
			return "ssl_error";
		}

		if (message.includes("refused") || error.code === "ECONNREFUSED") {
			return "connection_refused";
		}

		if (error.code === "ERR_CANCELED" || message.includes("aborted")) {
			return "request_aborted";
		}

		// Generic network error
		if (error.request) {
			return "network_error";
		}

		return "unknown_error";
	}

	return "http_error";
}

/**
 * Sanitize request/response data to avoid logging sensitive information
 */
function sanitizeData(data: unknown): unknown {
	if (!data || typeof data !== "object") return data;

	// Handle arrays
	if (Array.isArray(data)) {
		return data.map((item) => sanitizeData(item));
	}

	const sanitized = { ...(data as Record<string, unknown>) };
	const sensitiveKeys = [
		"password",
		"token",
		"secret",
		"authorization",
		"credit_card",
		"cvv",
		"api_key",
		"apikey",
		"bearer",
	];

	for (const key of Object.keys(sanitized)) {
		if (sensitiveKeys.some((sk) => key.toLowerCase().includes(sk))) {
			sanitized[key] = "[REDACTED]";
		} else if (typeof sanitized[key] === "object" && sanitized[key] !== null) {
			sanitized[key] = sanitizeData(sanitized[key]);
		}
	}
	return sanitized;
}

const api = axios.create({
	baseURL: env.VITE_BACKEND_API_URL,
	headers: {
		"Content-Type": "application/json",
	},
	timeout: 30000, // 30 second timeout
});

// Store the getToken function globally so interceptors can access it
let globalGetToken: (() => Promise<string | null>) | null = null;

export const setGlobalGetToken = (getToken: () => Promise<string | null>) => {
	globalGetToken = getToken;
};

// Request interceptor to attach Authorization header and track timing
api.interceptors.request.use(async (config) => {
	// Track request start time for duration calculation
	config._requestStartTime = Date.now();

	try {
		if (globalGetToken) {
			const token = await globalGetToken();
			if (token) {
				config.headers = config.headers || {};
				config.headers.Authorization = `Bearer ${token}`;
			}
		}
	} catch (_err) {
		// If token retrieval fails, proceed without token; response interceptor will handle 401
	}
	return config;
});

// Response interceptor to handle token refresh on 401 errors and track API errors
api.interceptors.response.use(
	(response: AxiosResponse) => {
		const duration = response.config._requestStartTime
			? Date.now() - response.config._requestStartTime
			: undefined;

		// Add successful API call as breadcrumb for context in error reports
		Sentry.addBreadcrumb({
			category: "api",
			message: `${response.config.method?.toUpperCase()} ${response.config.url}`,
			level: "info",
			data: {
				status: response.status,
				duration_ms: duration,
			},
		});
		return response;
	},
	async (error: AxiosError) => {
		const originalRequest = error.config;
		const errorType = classifyError(error);
		const duration = originalRequest?._requestStartTime
			? Date.now() - originalRequest._requestStartTime
			: undefined;

		// Check if error is 401 and we haven't already retried
		if (
			error.response?.status === 401 &&
			originalRequest &&
			!originalRequest._retry &&
			globalGetToken
		) {
			originalRequest._retry = true;

			try {
				// Get a fresh token
				const newToken = await globalGetToken();

				if (newToken && originalRequest.headers) {
					// Update the authorization header with the new token
					originalRequest.headers.Authorization = `Bearer ${newToken}`;

					// Retry the original request
					return api(originalRequest);
				}
			} catch (refreshError) {
				console.error("Token refresh failed:", refreshError);
			}
		}

		// Determine which HTTP errors should be captured as exceptions
		const shouldCaptureHttpError = (status: number): boolean => {
			if (status >= 500) return true;
			if ([400, 403, 404, 429].includes(status)) return true;
			return false;
		};

		// Skip breadcrumbs for 401s that are being retried
		const shouldAddBreadcrumb =
			error.response?.status !== 401 || originalRequest?._retry;

		// Build common context for all error types
		const buildErrorContext = () => ({
			endpoint: originalRequest?.url,
			method: originalRequest?.method?.toUpperCase(),
			baseURL: originalRequest?.baseURL,
			fullURL: `${originalRequest?.baseURL || ""}${originalRequest?.url || ""}`,
			duration_ms: duration,
			error_code: error.code,
			error_message: error.message,
			user_agent: navigator.userAgent,
			page_url: window.location.href,
		});

		// Handle network errors (CORS, timeout, DNS, etc.) - no response object
		if (!error.response) {
			Sentry.addBreadcrumb({
				category: "api",
				message: `Network Error: ${originalRequest?.method?.toUpperCase()} ${originalRequest?.url}`,
				level: "error",
				data: {
					error_type: errorType,
					error_code: error.code,
					error_message: error.message,
					duration_ms: duration,
				},
			});

			// Always capture network errors - they indicate infrastructure issues
			Sentry.captureException(error, {
				tags: {
					type: "api_error",
					error_type: errorType,
					endpoint: originalRequest?.url,
					method: originalRequest?.method?.toUpperCase(),
				},
				extra: {
					...buildErrorContext(),
					request_data: sanitizeData(originalRequest?.data),
					// Include headers (sanitized) for debugging CORS issues
					request_headers: sanitizeData(
						originalRequest?.headers as unknown as Record<string, unknown>,
					),
				},
				fingerprint: [
					"api_error",
					errorType,
					originalRequest?.url || "unknown",
				],
			});

			return Promise.reject(error);
		}

		// Handle HTTP errors (4xx, 5xx) - has response object
		if (shouldAddBreadcrumb) {
			Sentry.addBreadcrumb({
				category: "api",
				message: `HTTP Error: ${originalRequest?.method?.toUpperCase()} ${originalRequest?.url}`,
				level: "error",
				data: {
					error_type: errorType,
					status: error.response.status,
					status_text: error.response.statusText,
					duration_ms: duration,
				},
			});

			if (shouldCaptureHttpError(error.response.status)) {
				Sentry.captureException(error, {
					tags: {
						type: "api_error",
						error_type: errorType,
						status: String(error.response.status),
						endpoint: originalRequest?.url,
						method: originalRequest?.method?.toUpperCase(),
					},
					extra: {
						...buildErrorContext(),
						status: error.response.status,
						status_text: error.response.statusText,
						response_data: sanitizeData(error.response.data),
						response_headers: error.response.headers,
						request_data: sanitizeData(originalRequest?.data),
					},
					fingerprint: [
						"api_error",
						errorType,
						String(error.response.status),
						originalRequest?.url || "unknown",
					],
				});
			}
		}

		return Promise.reject(error);
	},
);

export default api;
